import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { userClient } from "@users/userClient";

export const usersKeys = {
  list: (sessionKey: string) => ["users", sessionKey] as const,
};

/**
 * Clinic users, used to resolve a note's signer uuid to a name. Non-suspense
 * and best-effort: a failed/slow users fetch must not block or crash the views
 * that only need it for an optional display name.
 */
export const useClinicUsers = (sessionKey: string) =>
  useQuery({
    queryKey: usersKeys.list(sessionKey),
    queryFn: () => userClient.list(),
  });

/**
 * Same roster as {@link useClinicUsers}, suspense-flavored for the admin
 * Users panel, which owns a panel-level `AsyncBoundary` and wants the list
 * to be its primary (blocking) content rather than an optional lookup.
 */
export const useUsersList = (sessionKey: string) =>
  useSuspenseQuery({
    queryKey: usersKeys.list(sessionKey),
    queryFn: () => userClient.list(),
  });

/**
 * Sets a user's PIN to an admin-chosen value. No cache invalidation: a reset
 * doesn't change any field the roster list renders.
 */
export const useResetPin = () =>
  useMutation({
    mutationFn: ({ userId, pin }: { userId: string; pin: string }) =>
      userClient.resetPin(userId, pin),
    meta: { action: "reset the PIN" },
  });
